import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { useAuth } from '../lib/auth.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { Icon } from '../components/Icon.js'

export const Route = createFileRoute('/_auth/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const { requestPasswordReset } = useAuth()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await requestPasswordReset(email)
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the email')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-fg">Forgot password</h1>
        <p className="text-sm text-fg-muted">
          Enter your email and we will send you a link to reset your password.
        </p>
      </header>

      {sent ? (
        <>
          <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
            <Icon name="alert" size={14} className="mt-0.5 text-fg-subtle" />
            <p>
              If an account exists for <strong className="text-fg">{email}</strong>,
              we have sent a reset link. Check your inbox, and your spam folder if
              it does not arrive in a few minutes.
            </p>
          </div>
          <p className="text-center text-sm text-fg-muted">
            <Link
              to="/login"
              className="font-medium text-[var(--color-primary)] hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@academy.com"
              />
            </Field>

            {error ? (
              <div className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
                {error}
              </div>
            ) : null}

            <Button type="submit" loading={submitting} className="w-full">
              Send reset link
            </Button>
          </form>

          <p className="text-center text-sm text-fg-muted">
            Remembered it?{' '}
            <Link
              to="/login"
              className="font-medium text-[var(--color-primary)] hover:underline"
            >
              Sign in
            </Link>
          </p>
        </>
      )}
    </div>
  )
}
